import { instance } from '~/utils/api/api';
import {
  IsSort,
  Table,
  TableBody,
  TableCell,
  TableCellHead,
  TableHead,
  TableRow,
} from '~/ui/Table';
import { IconButton, InputBar } from '~/ui';
import Eye from '~/assets/svg/newSvg/eye.svg?react';
import EyeHidden from '~/assets/svg/newSvg/eye-hidden.svg?react';
import Cart from '~/assets/svg/newSvg/cart.svg?react';
import CartOff from '~/assets/svg/newSvg/cart-off.svg?react';
import cn from 'classnames';
import styles from './CurrentMenuTable.module.scss';
import CustomCheckbox from '~/components/Shared/CustomCheckbox/CustomCheckbox';
import { renderPrice } from '../utils';
import Edit from '~/assets/svg/newSvg/edit.svg?react';
import Checkmark from '~/assets/svg/newSvg/mark-icon.svg?react';
import Block from '~/assets/svg/newSvg/block.svg?react';
import { useEffect, useState } from 'react';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import LoaderSpinner from '~/components/Shared/LoaderSpinner/LoaderSpinner';

interface FoodTypes {
  display_name: string;
}

export interface CurrentMenuItem {
  id: number;
  wares: {
    id: number;
    code: string;
    wares_name: string;
    price_list: { price: string };
    weight: string;
    parent: string;
    currency: string;
  };
  visibility: boolean;
  food_types: FoodTypes[];
  is_available: boolean;
  max_amount: number | null;
  ordered_qty: number | null;
  actual_price: string;
  measure: string;
  weight: number;
  position: number;
}

interface FoodWaresValues {
  id: number;
  visibility: boolean;
}

interface Params {
  sort: string;
  perPage: number;
  field: string;
  query: string;
  page: number | null;
  date?: string;
  food_id: number;
}

interface Props {
  currentMenu: CurrentMenuItem[];
  sort: IsSort;
  setSort: ({ field, direction }: IsSort) => void;
  foodId: number;
  params: Params;
  selectedFoods: number[];
  handleSelect: (id: number) => void;
  handleToggleAll: () => void;
  updateCurrentMenu: (data: CurrentMenuItem[]) => void;
  t: (text: string) => string;
  refetchMenu: () => void;
}

const CurrentMenuTable = ({
  currentMenu,
  sort,
  setSort,
  updateCurrentMenu,
  selectedFoods,
  handleSelect,
  handleToggleAll,
  refetchMenu,
  t,
}: Props): JSX.Element => {
  const [editingId, setEditingId] = useState<null | number>(null);
  const [foodItemQuantity, setFoodItemQuantity] = useState<string>('');
  const [foodItemPrice, setFoodItemPrice] = useState<string>('');
  const [foodItemPosition, setFoodItemPosition] = useState<string>('');
  const [foodItemWeight, setFoodItemWeight] = useState<string>('');
  const [foodItemMeasure, setFoodItemMeasure] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isInputsFilled, setIsInputsFilled] = useState(false);
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();

  const changeVisibilityStatus = async ({
    id,
    visibility,
  }: FoodWaresValues) => {
    const initialFoods = currentMenu;
    const updatedFoods = currentMenu.map(food => {
      if (food.id === id) {
        return { ...food, visibility: !food.visibility };
      }
      return food;
    });
    try {
      const response = await instance.post(
        `admin/set-food-wares-visibility/${id}`,
        {
          visibility,
        }
      );
      updateCurrentMenu(updatedFoods);
      return response.data;
    } catch (error) {
      updateCurrentMenu(initialFoods);
    }
  };

  const changeAvailableOrder = async ({
    id,
    available,
  }: {
    id: number;
    available: boolean;
  }) => {
    const initialFoods = currentMenu;
    const updatedFoods = currentMenu.map(food => {
      if (food.id === id) {
        return { ...food, is_available: !food.is_available };
      }
      return food;
    });
    try {
      const response = await instance.post(`admin/food-wares-available/${id}`, {
        available,
      });
      updateCurrentMenu(updatedFoods);
      return response.data;
    } catch (error) {
      updateCurrentMenu(initialFoods);
    }
  };

  const handleQuantityEdit = async () => {
    if (editingId === null) return;
    const initialFoods = currentMenu;
    const findigData = currentMenu.find(item => item.id === editingId);
    if (!findigData) return;
    const dishName = findigData.wares.wares_name;

    try {
      setIsUpdating(true);
      const response = await instance.put(
        `admin/food-wares-amount/${editingId}`,
        {
          max_amount: Number(foodItemQuantity),
        }
      );
      const priceResponse = await instance.put(
        `admin/food-wares-price/${editingId}`,
        {
          price: Number(foodItemPrice),
        }
      );
      const weightResponse = await instance.post(
        `admin/food-wares-weight/${editingId}`,
        {
          weight: Number(foodItemWeight),
        }
      );
      const measureResponse = await instance.post(
        `admin/food-wares-measure/${editingId}`,
        {
          measure: foodItemMeasure,
        }
      );
      const positionResponse = await instance.put(
        `admin/food-wares-position/${editingId}`,
        {
          position: Number(foodItemPosition),
        }
      );
      if (
        response.data?.updated &&
        priceResponse &&
        weightResponse &&
        measureResponse &&
        positionResponse
      ) {
        toastSuccess(
          `${t('the_dish')} "${dishName}" ${t('has_successfully_updated')}`
        );
        refetchMenu();
      }
    } catch (error) {
      updateCurrentMenu(initialFoods);
      toastError(error);
    } finally {
      setIsUpdating(false);
      setEditingId(null);
      setFoodItemQuantity('');
      setFoodItemPrice('');
      setFoodItemMeasure('');
      setFoodItemWeight('');
      setFoodItemPosition('');
    }
  };

  const handleQuantityClose = () => {
    setEditingId(null);
    setFoodItemQuantity('');
    setFoodItemPrice('');
    setFoodItemPosition('');
  };

  const handleQuantityChange = (value: string) => {
    if (/^\d*$/.test(value)) {
      setFoodItemQuantity(value);
    } else {
      return;
    }
  };

  const handlePriceChange = (value: string) => {
    if (value === '' || /^\d+(\.\d*)?$/.test(value)) {
      setFoodItemPrice(value);
    }
  };

  const handleMeasureChange = (value: string) => {
    if (value === '' || /^[A-Za-zА-Яа-яЁё]+$/.test(value)) {
      setFoodItemMeasure(value);
    } else {
      return;
    }
  };
  const handleWeightChange = (value: string) => {
    if (value === '' || /^\d+(\.\d*)?$/.test(value)) {
      setFoodItemWeight(value);
    } else {
      return;
    }
  };

  const handlePositionChange = (value: string) => {
    if (value === '' || /^\d+$/.test(value)) {
      setFoodItemPosition(value);
    } else {
      return;
    }
  };

  useEffect(() => {
    if (
      foodItemMeasure &&
      foodItemWeight &&
      foodItemPrice &&
      foodItemQuantity &&
      foodItemPosition
    ) {
      setIsInputsFilled(true);
    } else {
      setIsInputsFilled(false);
    }
  }, [
    foodItemMeasure,
    foodItemWeight,
    foodItemPrice,
    foodItemQuantity,
    foodItemPosition,
  ]);

  return (
    <div className={styles.tableWrapper}>
      <Table>
        <TableHead sort={sort} setSort={setSort}>
          <TableCellHead
            className={styles.tableCellHead}
            sortable={false}
            field="checkbox"
          >
            <CustomCheckbox
              isChecked={
                selectedFoods.length === currentMenu.length &&
                !!selectedFoods.length
              }
              onChange={handleToggleAll}
            />
          </TableCellHead>
          {
            <TableCellHead sortable={false} field="id">
              {t('position')}
            </TableCellHead>
          }
          <TableCellHead sortable={false} field="id">
            {t('code')}
          </TableCellHead>
          <TableCellHead field="wares_name">{t('name')}</TableCellHead>
          <TableCellHead sortable={false} field="parent">
            {t('category')}
          </TableCellHead>
          <TableCellHead field="price">{t('price')}</TableCellHead>
          <TableCellHead
            sortable={false}
            field="weight"
            className={
              editingId ? styles.measureHeadActive : styles.measureHead
            }
          >
            {t('measure')}
          </TableCellHead>
          <TableCellHead field="food_types">{t('service')}</TableCellHead>
          <TableCellHead field="quantity" sortable={false}>
            {t('quantity_full')}
          </TableCellHead>
          <TableCellHead sortable={false} field="actions">
            {t('action')}
          </TableCellHead>
        </TableHead>
        <TableBody>
          {currentMenu.map(
            ({
              id,
              position,
              wares,
              visibility,
              food_types,
              is_available,
              max_amount,
              ordered_qty,
              actual_price,
              measure,
              weight,
            }: CurrentMenuItem) => {
              const totalAmount =
                max_amount && ordered_qty
                  ? max_amount - ordered_qty
                  : !max_amount
                    ? 0
                    : !ordered_qty && max_amount
                      ? max_amount
                      : 0;

              return (
                <TableRow key={id}>
                  <TableCell hidden={!visibility || !is_available}>
                    <CustomCheckbox
                      isChecked={selectedFoods.includes(id)}
                      onChange={() => handleSelect(id)}
                    />
                  </TableCell>
                  {editingId !== id ? (
                    <TableCell hidden={!visibility || !is_available}>
                      {position}
                    </TableCell>
                  ) : (
                    <TableCell hidden={!visibility || !is_available}>
                      <InputBar
                        name="position"
                        value={foodItemPosition}
                        onChange={e => handlePositionChange(e.target.value)}
                        disabled={isUpdating}
                      />
                    </TableCell>
                  )}
                  <TableCell hidden={!visibility || !is_available}>
                    {wares?.code}
                  </TableCell>
                  <TableCell hidden={!visibility || !is_available}>
                    {wares?.wares_name}
                  </TableCell>
                  <TableCell
                    className={styles.typeCell}
                    hidden={!visibility || !is_available}
                  >
                    {wares?.parent ? wares?.parent : t('dish')}
                  </TableCell>
                  {editingId !== id ? (
                    <TableCell
                      className={styles.priceCell}
                      hidden={!visibility || !is_available}
                    >
                      {renderPrice(actual_price, wares?.currency)}
                    </TableCell>
                  ) : (
                    <TableCell
                      className={styles.priceCell}
                      hidden={!visibility || !is_available}
                    >
                      <InputBar
                        value={foodItemPrice}
                        name="price"
                        onChange={e => handlePriceChange(e.target.value)}
                        disabled={isUpdating}
                      />
                    </TableCell>
                  )}
                  {editingId !== id ? (
                    <TableCell hidden={!visibility || !is_available}>
                      {weight} {measure}
                    </TableCell>
                  ) : (
                    <TableCell>
                      <div className={styles.weightCell}>
                        <InputBar
                          name="weight"
                          value={foodItemWeight}
                          onChange={e => handleWeightChange(e.target.value)}
                          disabled={isUpdating}
                        />
                        <InputBar
                          name="measure"
                          value={foodItemMeasure}
                          onChange={e => handleMeasureChange(e.target.value)}
                          disabled={isUpdating}
                        />
                      </div>
                    </TableCell>
                  )}
                  <TableCell hidden={!visibility || !is_available}>
                    {food_types ? food_types[0]?.display_name : '-'}
                  </TableCell>
                  {editingId !== id ? (
                    <TableCell hidden={!visibility || !is_available}>
                      {totalAmount}
                    </TableCell>
                  ) : (
                    <TableCell hidden={!visibility || !is_available}>
                      <InputBar
                        name="quantity"
                        value={foodItemQuantity}
                        onChange={e => handleQuantityChange(e.target.value)}
                        disabled={isUpdating}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <div className={styles.actions}>
                      {editingId === id ? (
                        <>
                          {!isUpdating ? (
                            <IconButton
                              onClick={handleQuantityEdit}
                              className={cn(styles.eyeBtn)}
                              isDisabled={!isInputsFilled}
                            >
                              <Checkmark
                                className={
                                  isInputsFilled ? '' : styles.checkmarkDisabled
                                }
                              />
                            </IconButton>
                          ) : (
                            <LoaderSpinner />
                          )}

                          <IconButton
                            onClick={handleQuantityClose}
                            className={cn(styles.eyeBtn)}
                          >
                            <Block />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton
                          onClick={() => {
                            setEditingId(id);
                            setFoodItemWeight(String(weight || ''));
                            setFoodItemMeasure(String(measure || ''));
                            setFoodItemQuantity(String(max_amount || ''));
                            setFoodItemPrice(String(actual_price || ''));
                            setFoodItemPosition(String(position || ''));
                          }}
                          className={cn(styles.eyeBtn)}
                        >
                          <Edit />
                        </IconButton>
                      )}
                      {editingId !== id && (
                        <>
                          <IconButton
                            onClick={() =>
                              changeVisibilityStatus({
                                id,
                                visibility: !visibility,
                              })
                            }
                            className={cn(styles.eyeBtn)}
                          >
                            {visibility ? <Eye /> : <EyeHidden />}
                          </IconButton>
                          <IconButton
                            onClick={() =>
                              changeAvailableOrder({
                                id,
                                available: !is_available,
                              })
                            }
                          >
                            {is_available ? <Cart /> : <CartOff />}
                          </IconButton>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            }
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default CurrentMenuTable;
